import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const Field = ({ id, label, children }) => (
  <div>
    <Label htmlFor={id} className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const ServiceRows = ({ services, onChange }) => {
  const update = (i, patch) => onChange(services.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  return (
    <div className="space-y-2" data-testid="edit-services">
      {services.map((s, i) => (
        <div key={s.id || i} className="grid grid-cols-[1fr_84px_96px_32px] gap-2 items-center">
          <Input data-testid={`edit-service-name-${i}`} value={s.name} placeholder="Service name" onChange={(e) => update(i, { name: e.target.value })} />
          <Input data-testid={`edit-service-price-${i}`} type="number" min="0" step="0.01" value={s.price} onChange={(e) => update(i, { price: e.target.value })} />
          <Input data-testid={`edit-service-duration-${i}`} value={s.duration || ""} placeholder="60 min" onChange={(e) => update(i, { duration: e.target.value })} />
          <button type="button" data-testid={`edit-service-remove-${i}`} className="text-muted-foreground hover:text-foreground" onClick={() => onChange(services.filter((_, j) => j !== i))}>
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      <Button type="button" size="sm" variant="ghost" className="rounded-full" data-testid="edit-service-add" onClick={() => onChange([...services, { name: "", price: 0, duration: "" }])}>
        <Plus size={13} className="mr-1" /> Add service
      </Button>
    </div>
  );
};

export const EditPractitionerDialog = ({ practitioner: p, onClose, onSaved }) => {
  const [form, setForm] = useState({
    business_name: p.business_name || "", bio: p.bio || "", phone: p.phone || "",
    city_name: p.city_name || "", country: p.country || "", services: p.services || [],
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/practitioners/${p.id}`, { ...form, services: form.services.map((s) => ({ ...s, price: Number(s.price) || 0 })) });
      toast.success("Changes saved — application is still awaiting review");
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="edit-practitioner-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Edit application</DialogTitle>
          <DialogDescription>Fix small things like spelling or grammar. The application stays in "awaiting review" until you approve or reject it.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <Field id="edit-business-name" label="Business name">
            <Input id="edit-business-name" data-testid="edit-business-name" value={form.business_name} onChange={set("business_name")} />
          </Field>
          <Field id="edit-bio" label="Bio">
            <Textarea id="edit-bio" data-testid="edit-bio" rows={6} value={form.bio} onChange={set("bio")} />
          </Field>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field id="edit-phone" label="Phone"><Input id="edit-phone" data-testid="edit-phone" value={form.phone} onChange={set("phone")} /></Field>
            <Field id="edit-city" label="City"><Input id="edit-city" data-testid="edit-city" value={form.city_name} onChange={set("city_name")} /></Field>
            <Field id="edit-country" label="Country"><Input id="edit-country" data-testid="edit-country" value={form.country} onChange={set("country")} /></Field>
          </div>
          <Field label="Services"><ServiceRows services={form.services} onChange={(services) => setForm((f) => ({ ...f, services }))} /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" data-testid="edit-practitioner-cancel" disabled={busy} onClick={onClose}>Cancel</Button>
          <Button className="rounded-full" data-testid="edit-practitioner-save" disabled={busy || !form.business_name.trim()} onClick={save}>{busy ? "Saving…" : "Save changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
